import {noop} from '@benchling/js-utils';
import {Alert, Anchor, createStyles, Group, SegmentedControl} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import PlateMetadataFieldsModal from 'notebook/integrations/plate-maps/scratch-plates/PlateMetadataFieldsModal';
import ScratchPlateRecordsView, {
  buildScratchPlateRecordsItems,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateRecordsView';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {DisplayLayerOptionSection} from 'samples/plate-maps/PlateMapConstants';
import PlateMapSummary from 'samples/plate-maps/PlateMapSummary';
import {getReadonlyParameterLayerDisplayOptions} from 'samples/plate-maps/PlateMapUtil';
import {ScratchPlateDesignState} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import SaveScratchPlateAsDesignTemplateModal from './SaveScratchPlateAsDesignTemplateModal';
import {
  useScratchPlateCreatedPlates,
  useScratchPlateData,
  useWellSchemaOptionsForPlateMap,
} from './ScratchPlateDesignSummaryUtil';

export interface ScratchPlateDesignSummaryProps {
  scratchPlateId: string;
  locked: boolean;
  viewer: boolean;
  collapseId?: string;
  onEdit: (initialState: ScratchPlateDesignState, scratchPlateId: string, refetch: () => void) => void;
  onFillPlates?: (
    initialDesignState: ScratchPlateDesignState,
    originDesignApiId: string,
    refetch: () => void
  ) => void;
  shouldQueryCreatedPlates?: boolean;
  /**
   * API id to export the audit log for, if different from the underlying `scratchPlateId` -- e.g. when
   * displaying a design's owning `PlateDesignTemplate`, whose own audit trail is what a viewer expects
   * "View and export audit log" to show. Defaults to `scratchPlateId`.
   */
  auditLogApiId?: string;
}

const useStyles = createStyles((theme) => ({
  errorBannerWrapper: {
    alignSelf: 'flex-start',
  },
  viewToggle: {
    marginBottom: theme.spacing.xs,
  },
}));

enum PlateView {
  DESIGN = 'design',
  RECORDS = 'records',
}

export default function ScratchPlateDesignSummary({
  scratchPlateId,
  locked,
  viewer,
  onEdit,
  onFillPlates,
  collapseId,
  shouldQueryCreatedPlates = true,
  auditLogApiId = scratchPlateId,
}: ScratchPlateDesignSummaryProps): JSX.Element {
  const {classes} = useStyles();
  // `scratchPlateDesign` is the design object; the records list comes from a dedicated created-plates query.
  const {
    plate: scratchPlateDesign,
    plateMap,
    containerSchemaId,
    plateSchema,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    schemaStorePending,
    schemaStoreLoadFailed,
    plateMetadataFields,
    plateSchemaFieldValues,
    refetch,
    refetchDataFrames,
  } = useScratchPlateData(scratchPlateId);
  const {createdPlates, refetch: refetchCreatedPlates} = useScratchPlateCreatedPlates(
    scratchPlateId,
    shouldQueryCreatedPlates
  );
  const [saveAsDesignModalOpen, setSaveAsDesignModalOpen] = useState(false);
  const [metadataFieldsModalOpen, setMetadataFieldsModalOpen] = useState(false);
  const [activeView, setActiveView] = useState<PlateView>(PlateView.DESIGN);
  const [selectedPlateId, setSelectedPlateId] = useState<string | null>(null);

  const wellSchemaLayers = useWellSchemaLayers(containerSchemaId, noop, []);
  const wellSchemaOptions = useWellSchemaOptionsForPlateMap(plateMap, wellSchemaLayers);
  const plateDataParametersOptions = useMemo(
    () => (plateMap != null ? getReadonlyParameterLayerDisplayOptions(plateMap) : []),
    [plateMap]
  );

  // Data frames still resolving, or the plate schema store still loading, means `plateSchema` (and
  // therefore `buildDesignState`) can't yet be trusted to reflect the design's real, current schema.
  const isPlateDataLoading = dataFrameQueriesPending || schemaStorePending;
  // A failed or inaccessible data-frame query, or a schema that never resolved (see
  // `schemaStoreLoadFailed`), isn't "loading" (isPlateDataLoading is false in all three), but each
  // leaves `plateMap`/`plateSchema` unreliable for `buildDesignState` — an inaccessible frame is
  // dropped rather than surfaced as null, so `plateMap` still builds non-null from the remaining
  // layers — so all three must block actions the same way loading does.
  const actionsBlocked =
    isPlateDataLoading || dataFrameQueriesError || dataFrameIsInaccessible || schemaStoreLoadFailed;
  const isDisabled =
    !BenchlingConfig.ENABLE_SCRATCH_PLATES ||
    locked ||
    scratchPlateDesign == null ||
    plateMap == null ||
    actionsBlocked;
  const recordsItems = useMemo(() => buildScratchPlateRecordsItems(createdPlates), [createdPlates]);
  const hasRecords = createdPlates.length > 0;
  function refetchAllData(): void {
    refetch();
    refetchCreatedPlates();
  }
  const effectiveView: PlateView = hasRecords ? activeView : PlateView.DESIGN;
  const firstPlateId = recordsItems.find((item) => item.kind === 'plate')?.id ?? null;
  const validSelectedPlateId =
    selectedPlateId != null &&
    recordsItems.some((item) => item.kind === 'plate' && item.id === selectedPlateId)
      ? selectedPlateId
      : firstPlateId;
  const viewToggle = hasRecords ? (
    <SegmentedControl
      aria-label="Plate view mode"
      className={classes.viewToggle}
      fullWidth={true}
      value={effectiveView}
      data={[
        {label: 'Design', value: PlateView.DESIGN},
        {label: `Plates (${createdPlates.length})`, value: PlateView.RECORDS},
      ]}
      onChange={(value) => {
        if (value === PlateView.DESIGN || value === PlateView.RECORDS) {
          setActiveView(value);
        }
      }}
    />
  ) : null;

  function buildDesignState(
    nonNullPlate: NonNullable<typeof scratchPlateDesign>,
    nonNullPlateMap: NonNullable<typeof plateMap>
  ): ScratchPlateDesignState {
    const {name, height, width} = nonNullPlate;
    const plateSize: ScratchPlateDesignState['plateSize'] = [height, width];
    // `PlateFieldValue` is shaped to match `PlateMetadataFieldEntry` field-for-field, so it needs no remapping.
    return {
      name,
      plateSize,
      plateSchema: plateSchema ?? null,
      plateMap: nonNullPlateMap,
      plateMetadataFields: plateMetadataFields ?? [],
      plateSchemaFieldValues: plateSchemaFieldValues ?? [],
    };
  }

  function handleEdit(): void {
    if (scratchPlateDesign == null || plateMap == null || actionsBlocked) {
      return;
    }
    onEdit(buildDesignState(scratchPlateDesign, plateMap), scratchPlateId, refetchAllData);
  }

  function handleFillPlates(): void {
    if (scratchPlateDesign == null || plateMap == null || onFillPlates == null || actionsBlocked) {
      return;
    }
    onFillPlates(buildDesignState(scratchPlateDesign, plateMap), scratchPlateId, refetchAllData);
  }

  const headerControls = (
    <Group spacing={8} noWrap={true}>
      {!viewer && (
        <Button
          size={ButtonSize.MINI}
          icon="pen"
          aria-label="Edit plate design"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={isDisabled}
          onClick={handleEdit}
        />
      )}
      <Button
        size={ButtonSize.MINI}
        icon="info-circle"
        aria-label="View plate metadata"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        // The plate's info can always be viewed
        disabled={false}
        onClick={() => {
          setMetadataFieldsModalOpen(true);
        }}
      />
      <OverflowMenu
        enableTooltip={false}
        buttonProps={{
          'aria-label': 'More options',
          className: undefined,
        }}
        menuProps={{dropLeft: true}}
        options={[
          // The read-only viewer only offers exporting the audit log -- editing actions like
          // "Save as template" don't apply to a display the user can't otherwise mutate from.
          ...(!viewer
            ? [
                {
                  contents: <>Save as template</>,
                  onSelect: () => {
                    setSaveAsDesignModalOpen(true);
                  },
                  isDisabled,
                },
              ]
            : []),
          {
            contents: <>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</>,
            onSelect: () => {
              exportAuditLogs([auditLogApiId]);
            },
          },
        ]}
      />
      {!viewer && onFillPlates != null && (
        <Button
          size={ButtonSize.MINI}
          icon="fill-grid"
          text="Fill plates"
          buttonStyle={ButtonStyle.PRIMARY}
          disabled={isDisabled}
          onClick={handleFillPlates}
          dataTestAttrs={{component: 'ScratchPlateDesignSummary', element: 'fillPlatesButton'}}
        />
      )}
    </Group>
  );

  return (
    <div {...getTestProps({component: 'ScratchPlateDesignSummary', element: 'root'})}>
      <PlateMapContainer
        header="Plate design"
        headerControls={headerControls}
        subheader={scratchPlateDesign?.name ?? scratchPlateId}
        onHeaderContextMenu={noop}
        plateMapType={PlateMapType.DESIGN}
        collapseId={collapseId}
      >
        {dataFrameIsInaccessible ? (
          <div className={classes.errorBannerWrapper}>
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="You do not have access to this plate design."
            />
          </div>
        ) : dataFrameQueriesError ? (
          <div className={classes.errorBannerWrapper}>
            <Alert variant="error" role="alert">
              Failed to load this plate design.{' '}
              <Anchor component="button" type="button" size="sm" onClick={refetchDataFrames}>
                Retry
              </Anchor>
            </Alert>
          </div>
        ) : schemaStoreLoadFailed ? (
          <div className={classes.errorBannerWrapper}>
            <Alert variant="error" role="alert">
              Failed to load plate schema information. Try refreshing the page.
            </Alert>
          </div>
        ) : effectiveView === PlateView.RECORDS ? (
          scratchPlateDesign != null ? (
            <ScratchPlateRecordsView
              recordsItems={recordsItems}
              selectedPlateId={validSelectedPlateId}
              onSelectPlateId={setSelectedPlateId}
              viewToggle={viewToggle}
              onFillAdditionalPlates={!viewer && onFillPlates != null ? handleFillPlates : undefined}
              fillAdditionalPlatesDisabled={isDisabled}
              designPlateSize={[scratchPlateDesign.height, scratchPlateDesign.width]}
              onPutInInventoryComplete={refetchCreatedPlates}
            />
          ) : (
            <CenteredHorizontalThrobber />
          )
        ) : plateMap != null && !isPlateDataLoading ? (
          <PlateMapSummary
            plateMap={plateMap}
            plateDataParametersOptions={plateDataParametersOptions}
            wellSchemaOptions={wellSchemaOptions}
            frozenReagentAssignmentGroups={null}
            procedureStudyDesignType={null}
            plateMapType={PlateMapType.DESIGN}
            hiddenSections={[DisplayLayerOptionSection.CONTENTS]}
            additionalMetadataContent={<></>}
            isDisplayingScratchPlate={true}
            stickyHeader={viewToggle}
          />
        ) : (
          <CenteredHorizontalThrobber />
        )}
      </PlateMapContainer>
      {saveAsDesignModalOpen && (
        <SaveScratchPlateAsDesignTemplateModal
          scratchPlateId={scratchPlateId}
          defaultName={scratchPlateDesign?.name ?? ''}
          opened={saveAsDesignModalOpen}
          onClose={() => {
            setSaveAsDesignModalOpen(false);
          }}
        />
      )}
      <PlateMetadataFieldsModal
        opened={metadataFieldsModalOpen}
        onClose={() => {
          setMetadataFieldsModalOpen(false);
        }}
        plateSchema={plateSchema}
        fields={[...(plateMetadataFields ?? []), ...(plateSchemaFieldValues ?? [])]}
      />
    </div>
  );
}
